import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  Button, 
  CircularProgress, 
  InputAdornment, 
  TextField, 
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Chip,
  IconButton,
  Collapse,
  Grid,
  Divider
} from '@mui/material';
import axios from 'axios';
import AdminUserEditDialog from '../components/AdminUserEditDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import Toast from '../components/Toast';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get('https://api.safenetbanking.com/api/user/all', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(res => {
      setUsers(res.data);
      setLoading(false);
    }).catch(err => {
      setLoading(false);
      setUsers([]);
    });
  }, []);

  const handleEdit = user => {
    setEditUser(user);
    setDialogOpen(true);
  };
  const handleDialogClose = () => setDialogOpen(false);
  const handleSave = async updated => {
    const token = localStorage.getItem('token');
    await axios.patch(`https://api.safenetbanking.com/api/admin/users/${updated.id}`, updated, {
      headers: { Authorization: 'Bearer ' + token }
    });
    setUsers(users.map(u => u.id === updated.id ? { ...u, ...updated } : u));
    setDialogOpen(false);
  };
  const handleDelete = id => {
    setDeleteId(id);
    setConfirmOpen(true);
  };
  const handleConfirmDelete = async () => {
    setDeleting(true);
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`https://api.safenetbanking.com/api/admin/users/${deleteId}`, {
        headers: { Authorization: 'Bearer ' + token }
      });
      setUsers(users.filter(u => u.id !== deleteId));
      setToast({ open: true, message: 'User deleted', severity: 'success' });
    } catch {
      setToast({ open: true, message: 'Delete failed', severity: 'error' });
    }
    setDeleting(false);
    setConfirmOpen(false);
  };

  // Filter users based on search query
  const filteredUsers = users.filter(u =>
    (u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
     u.email?.toLowerCase().includes(search.toLowerCase()) ||
     u.account_number?.toLowerCase().includes(search.toLowerCase()) ||
     u.id.toString().includes(search)
    )
  );

  // Function to toggle expanded user card on mobile
  const toggleExpand = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  // Function to get status chip color
  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'success';
      case 'dormant': return 'warning';
      case 'closed': return 'error';
      case 'disabled': return 'error';
      case 'suspicious': return 'warning';
      case 'frozen': return 'info';
      case 'irs': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h5" mb={3} sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
        All Users
      </Typography>
      
      <TextField 
        label="Search Users" 
        value={search} 
        onChange={e => setSearch(e.target.value)} 
        fullWidth 
        margin="normal" 
        size={isMobile ? "small" : "medium"}
        InputProps={{ 
          startAdornment: <InputAdornment position="start">🔍</InputAdornment> 
        }} 
      />
      
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Desktop View - Table */}
          {!isMobile && (
            <Paper sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Account #</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map(user => (
                    <TableRow key={user.id}>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>{user.full_name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.account_number}</TableCell>
                      <TableCell>{user.role}</TableCell>
                      <TableCell>
                        <Chip 
                          label={user.status} 
                          size="small" 
                          color={getStatusColor(user.status)}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={() => handleEdit(user)} 
                          sx={{ mr: 1 }}
                          startIcon={<EditIcon />}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="small" 
                          color="error" 
                          variant="outlined" 
                          onClick={() => handleDelete(user.id)} 
                          disabled={deleting}
                          startIcon={<DeleteIcon />}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
          
          {/* Mobile View - Cards */}
          {isMobile && (
            <Box sx={{ mt: 2 }}>
              {filteredUsers.length === 0 ? (
                <Typography align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No users found matching your search
                </Typography>
              ) : (
                filteredUsers.map(user => (
                  <Card key={user.id} sx={{ mb: 2, overflow: 'visible' }}>
                    <CardContent sx={{ pb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                          {user.full_name}
                        </Typography>
                        <Chip 
                          label={user.status} 
                          size="small" 
                          color={getStatusColor(user.status)}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                      
                      <Typography variant="body2">
                        Account: {user.account_number}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                        <Box>
                          <IconButton 
                            size="small" 
                            onClick={() => handleEdit(user)}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleDelete(user.id)}
                            disabled={deleting}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        
                        <IconButton 
                          size="small" 
                          onClick={() => toggleExpand(user.id)}
                          sx={{ transform: expandedUser === user.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}
                        >
                          {expandedUser === user.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                      
                      <Collapse in={expandedUser === user.id}>
                        <Divider sx={{ my: 1.5 }} />
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">ID</Typography>
                            <Typography variant="body2">{user.id}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Role</Typography>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{user.role}</Typography>
                          </Grid>
                          <Grid item xs={12} sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">Balance</Typography>
                            <Typography variant="body2">${user.balance || '0.00'}</Typography>
                          </Grid>
                        </Grid>
                      </Collapse>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          )}
        </>
      )}
      
      <AdminUserEditDialog 
        open={dialogOpen} 
        onClose={handleDialogClose} 
        user={editUser} 
        onSave={handleSave} 
      />
      
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete User"
        content="Are you sure you want to delete this user? This action cannot be undone."
      />
      
      <Toast 
        open={toast.open} 
        message={toast.message} 
        severity={toast.severity} 
        onClose={() => setToast({ ...toast, open: false })} 
      />
    </Box>
  );
}
